'use client';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { stripHtml } from '@/lib/strip-html';
import { format } from 'date-fns';
import { ArrowRight, Calendar, Eye, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import { TypingIndicator } from '@/app/(user)/chat/components/TypingIndicator';
import { Skeleton } from '@/components/ui/skeleton';

function formatViews(number) {
  if (number < 1000) return number.toString();
  if (number < 1_000_000) return (number / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (number < 1_000_000_000) return (number / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  return (number / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
}

function buildVideosUrl({ category, subCategory } = {}) {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (subCategory) params.set('subCategory', subCategory);
  const query = params.toString();
  return query ? `/videos?${query}` : '/videos';
}

export default function VideosByCategory({
  categories,
  categorySlug,
  subCategorySlug,
  subCategories,
  categoryVideos,
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [pendingType, setPendingType] = useState(null);

  const subCategoryRef = useRef(null);
  const videosRef = useRef(null);

  const navigate = (url, type) => {
    setPendingType(type);
    startTransition(() => {
      router.push(url);
    });
  };

  useEffect(() => {
    if (!isPending) {
      setPendingType(null);
    }
  }, [isPending, categorySlug, subCategorySlug, subCategories, categoryVideos]);

  const scrollToView = (ref) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  };

  useEffect(() => {
    if (subCategories?.subCategories?.length > 0) {
      scrollToView(subCategoryRef);
    }
  }, [subCategories]);

  useEffect(() => {
    if (categoryVideos?.videos?.length > 0) {
      scrollToView(videosRef);
    }
  }, [categoryVideos]);

  const handleReset = () => {
    navigate('/videos', 'reset');
  };

  let videosList = categoryVideos?.videos ?? [];

  if (categorySlug === 'ocia-rcia-classes') {
    videosList = videosList.sort((a, b) => a.sequence - b.sequence);
  }

  return (
    <div className={cn('flex flex-col gap-2')}>
      <div className="flex flex-wrap gap-2">
        {categories?.map((item) => (
          <Button
            key={item.slug}
            variant="outline"
            onClick={() => navigate(buildVideosUrl({ category: item.slug }), 'category')}
            disabled={isPending}
            className={cn(
              'h-auto w-auto max-w-full min-w-18 cursor-pointer px-2 py-2 text-xs sm:py-3 md:text-sm',
              'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue hover:border-periwinkle rounded-full text-center break-words',
              categorySlug === item.slug && 'bg-periwinkle text-dark-blue border-0'
            )}
          >
            {item.title}
          </Button>
        ))}

        <Button
          variant="icon"
          onClick={handleReset}
          disabled={isPending}
          className="border-dark-blue hover:bg-periwinkle hover:text-dark-blue hover:border-periwinkle h-8 w-8 cursor-pointer rounded-full border sm:h-11 sm:w-11"
        >
          <X className="text-primary size-5" />
        </Button>
      </div>

      {isPending && pendingType === 'category' && <TypingIndicator />}

      {subCategories &&
        subCategories.subCategories?.length > 0 &&
        categorySlug !== 'ocia-rcia-classes' && (
          <div ref={subCategoryRef}>
            <div className="mt-5 space-y-4">
              <div className="flex justify-start gap-4">
                <div className="message-shadow max-w-[85%] rounded-2xl border border-gray-100 bg-white px-8 py-3 text-gray-900">
                  <p className="text-md leading-relaxed">
                    Which aspect of <b className="capitalize">{subCategories.title}</b> would you
                    like to focus on?
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {subCategories.subCategories.map((item) => (
                <Button
                  key={item.slug}
                  variant="outline"
                  onClick={() =>
                    navigate(
                      buildVideosUrl({ category: categorySlug, subCategory: item.slug }),
                      'subcategory'
                    )
                  }
                  disabled={isPending}
                  className={cn(
                    'h-auto w-auto max-w-full min-w-18 cursor-pointer px-2 py-2 text-xs sm:py-3 md:text-sm',
                    'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue hover:border-periwinkle rounded-full text-center break-words',
                    subCategorySlug === item.slug && 'bg-periwinkle text-dark-blue border-0'
                  )}
                >
                  {item.title}
                </Button>
              ))}
            </div>

            {isPending && pendingType === 'subcategory' && <TypingIndicator />}
          </div>
        )}

      {isPending && pendingType === 'reset' && (
        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-3 rounded-lg border pb-3">
              <Skeleton className="h-[220px] w-full rounded-t-lg" />
              <div className="space-y-2 px-4">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {categoryVideos && videosList.length > 0 && (
        <div ref={videosRef}>
          <div className="mt-5 space-y-4">
            <div className="flex justify-start gap-4">
              <div className="message-shadow max-w-[85%] rounded-2xl border border-gray-100 bg-white px-8 py-3 text-gray-900">
                <p className="sm:text-md text-sm leading-relaxed">
                  {categorySlug === 'ocia-rcia-classes' ? (
                    <span className="text-sm">
                      Great Choice! Which aspect of would you like to focus on?
                    </span>
                  ) : (
                    <span className="text-sm">
                      Great Choice! Which aspect of{' '}
                      <b className="capitalize">
                        {categoryVideos?.subCategories?.title || categoryVideos?.category?.title}
                      </b>{' '}
                      would you like to focus on?
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            {videosList.map((video) => (
              <Card
                key={video.slug}
                className="group col-span-1 w-full gap-1 p-0 pb-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <CardHeader className="p-0">
                  <Link href={`/videos/${video.slug}`}>
                    <div className="relative h-[220px] w-full overflow-hidden rounded-t-lg">
                      {video.thumbnailUrl ? (
                        <Image
                          src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${video.thumbnailUrl}`}
                          alt={video.title}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                      ) : (
                        <div className="h-full w-full bg-gray-200" />
                      )}
                    </div>
                  </Link>
                </CardHeader>

                <CardContent>
                  <div className="text-muted-foreground mb-3 flex w-full items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {video?.createdAt && format(new Date(video.createdAt), 'dd MMM yyyy')}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Eye />
                      <span>{formatViews(video?.views ?? 0)}</span>
                    </div>
                  </div>

                  <CardTitle className="group-hover:text-primary line-clamp-2 text-xl font-bold transition-colors">
                    <Link href={`/videos/${video.slug}`}>{video.title}</Link>
                  </CardTitle>

                  {video.description && (
                    <CardDescription className="mt-2 line-clamp-3">
                      {stripHtml(video.description)}
                    </CardDescription>
                  )}
                </CardContent>

                <CardFooter className="flex items-center justify-end pt-2">
                  <Link
                    href={`/videos/${video.slug}`}
                    className="hover:text-primary flex items-center gap-1 text-sm font-semibold"
                  >
                    Watch now
                    <ArrowRight className="relative top-[1px] h-4 w-4" />
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
